import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  User, 
  Briefcase, 
  GraduationCap, 
  Award, 
  Globe, 
  TrendingUp, 
  Clock, 
  Star,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Target,
  Brain,
  Sparkles
} from 'lucide-react';
import { resumeService } from '../../../services/resumeService';

const ParsedResumeViewer = ({ candidateId, selectedJobId }) => {
  const [parsedData, setParsedData] = useState([]);
  const [skills, setSkills] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [jobMatch, setJobMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatedSummary, setGeneratedSummary] = useState('');
  const [summaryError, setSummaryError] = useState('');

  useEffect(() => {
    if (candidateId) {
      loadResumeData();
    }
  }, [candidateId]);

  useEffect(() => {
    if (candidateId && selectedJobId) {
      loadJobMatch();
    }
  }, [candidateId, selectedJobId]);

  const loadResumeData = async () => {
    try {
      setLoading(true);
      const [parsedResult, skillsResult, documentsResult] = await Promise.all([
        resumeService?.getParsedResumeData(candidateId),
        resumeService?.getCandidateSkills(candidateId),
        resumeService?.getResumeDocuments(candidateId)
      ]);

      setParsedData(parsedResult || []);
      setSkills(skillsResult || []);
      setDocuments(documentsResult || []);
    } catch (error) {
      console.error('Error loading resume data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobMatch = async () => {
    if (!candidateId || !selectedJobId) return;
    
    try {
      const matchResult = await resumeService?.calculateJobMatch(candidateId, selectedJobId);
      setJobMatch(matchResult);
    } catch (error) {
      console.error('Error calculating job match:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadResumeData();
    if (selectedJobId) {
      await loadJobMatch();
    }
    setRefreshing(false);
  };

  const handleGenerateSummary = async () => {
    if (!candidateId) return;

    try {
      setGeneratingSummary(true);
      setSummaryError('');
      setGeneratedSummary('');

      const result = await resumeService?.generateCandidateSummary(candidateId);

      if (result?.success) {
        setGeneratedSummary(result?.summary);
      } else {
        setSummaryError(result?.error || 'Failed to generate summary');
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      setSummaryError('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Analysis Complete';
      case 'processing':
        return 'Analyzing...';
      case 'failed':
        return 'Analysis Failed';
      default:
        return 'Pending Analysis';
    }
  };

  const getSeniorityBadgeColor = (level) => {
    const colors = {
      junior: 'bg-green-100 text-green-800',
      mid: 'bg-blue-100 text-blue-800',
      senior: 'bg-purple-100 text-purple-800',
      lead: 'bg-orange-100 text-orange-800',
      principal: 'bg-red-100 text-red-800',
      executive: 'bg-gray-900 text-white'
    };
    return colors?.[level] || colors?.junior;
  };

  const getSkillCategoryColor = (category) => {
    const colors = {
      frontend: 'bg-blue-100 text-blue-800',
      backend: 'bg-green-100 text-green-800',
      database: 'bg-purple-100 text-purple-800',
      cloud: 'bg-orange-100 text-orange-800',
      mobile: 'bg-pink-100 text-pink-800',
      devops: 'bg-gray-100 text-gray-800',
      design: 'bg-indigo-100 text-indigo-800',
      management: 'bg-yellow-100 text-yellow-800',
      other: 'bg-gray-50 text-gray-700'
    };
    return colors?.[category] || colors?.other;
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  const latestParsedData = parsedData?.[0];
  const primarySkills = skills?.filter(skill => skill?.is_primary_skill) || [];
  const secondarySkills = skills?.filter(skill => !skill?.is_primary_skill) || [];

  return (
    <div className="space-y-6">
      {/* Header with Generate Summary Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Brain className="h-5 w-5 mr-2 text-blue-500" />
          AI Resume Analysis
        </h3>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGenerateSummary}
            disabled={generatingSummary || !latestParsedData}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {generatingSummary ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                <span>Generate Summary</span>
              </>
            )}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Generated Summary Section */}
      {(generatedSummary || summaryError) && (
        <div className={`rounded-lg p-4 ${summaryError ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`text-sm font-medium ${summaryError ? 'text-red-900' : 'text-blue-900'} flex items-center`}>
              {summaryError ? (
                <>
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Summary Generation Error
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI-Generated Candidate Summary
                </>
              )}
            </h4>
          </div>
          <div className={`text-sm whitespace-pre-line ${summaryError ? 'text-red-800' : 'text-blue-800'}`}>
            {summaryError || generatedSummary}
          </div>
          {generatedSummary && (
            <div className="mt-3 text-xs text-blue-600">
              ✓ Summary saved to candidate notes
            </div>
          )}
        </div>
      )}

      {/* Documents Status */}
      {documents?.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Resume Documents</h4>
          <div className="space-y-2">
            {documents?.map((doc) => (
              <div key={doc?.id} className="flex items-center justify-between bg-white rounded p-3">
                <div className="flex items-center space-x-3">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc?.file_name}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      {getStatusIcon(doc?.parsing_status)}
                      <span className="text-xs text-gray-500">
                        {getStatusText(doc?.parsing_status)}
                      </span>
                    </div>
                  </div>
                </div>
                {doc?.downloadUrl && (
                  <a
                    href={doc?.downloadUrl}
                    download
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Job Match Score */}
      {jobMatch && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <Target className="h-4 w-4 mr-2 text-purple-500" />
            Job Match Analysis
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Match Score</span>
              <span className={`text-lg font-bold ${getMatchScoreColor(jobMatch?.match_score || 0)}`}>
                {jobMatch?.match_score || 0}%
              </span>
            </div>
            
            {jobMatch?.matched_skills?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-green-800 mb-2">Matching Skills</p>
                <div className="flex flex-wrap gap-1">
                  {jobMatch?.matched_skills?.map((skill, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {jobMatch?.missing_skills?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-800 mb-2">Missing Skills</p>
                <div className="flex flex-wrap gap-1">
                  {jobMatch?.missing_skills?.map((skill, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {jobMatch?.experience_gap > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Experience Gap</span>
                <span className="text-red-600 font-medium">+{jobMatch?.experience_gap} years needed</span>
              </div>
            )}
          </div>
        </div>
      )}

      {latestParsedData ? (
        <div className="space-y-6">
          {/* Summary */}
          {latestParsedData?.summary && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <User className="h-4 w-4 mr-2 text-blue-500" />
                Professional Summary
              </h4>
              <p className="text-sm text-gray-700">{latestParsedData?.summary}</p>
            </div>
          )}

          {/* Experience & Seniority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Clock className="h-4 w-4 mr-2 text-green-500" />
                <h4 className="text-sm font-medium text-gray-900">Experience</h4>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {latestParsedData?.years_of_experience || 0} years
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <TrendingUp className="h-4 w-4 mr-2 text-purple-500" />
                <h4 className="text-sm font-medium text-gray-900">Seniority Level</h4>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getSeniorityBadgeColor(latestParsedData?.seniority_level)}`}>
                {latestParsedData?.seniority_level?.charAt(0)?.toUpperCase() + latestParsedData?.seniority_level?.slice(1)}
              </span>
            </div>
          </div>

          {/* Skills */}
          {skills?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Star className="h-4 w-4 mr-2 text-yellow-500" />
                Skills Analysis
              </h4>
              
              {primarySkills?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-600 mb-2">Primary Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {primarySkills?.map((skill) => (
                      <div key={skill?.id} className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getSkillCategoryColor(skill?.skill_category)}`}>
                          {skill?.skill_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {skill?.years_experience}y
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {secondarySkills?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Additional Skills</p>
                  <div className="flex flex-wrap gap-1">
                    {secondarySkills?.map((skill) => (
                      <span key={skill?.id} className={`px-2 py-1 text-xs rounded ${getSkillCategoryColor(skill?.skill_category)}`}>
                        {skill?.skill_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Work Experience */}
          {latestParsedData?.work_experience && Array.isArray(latestParsedData?.work_experience) && latestParsedData?.work_experience?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <Briefcase className="h-4 w-4 mr-2 text-indigo-500" />
                Work Experience
              </h4>
              <div className="space-y-3">
                {latestParsedData?.work_experience?.map((exp, index) => (
                  <div key={index} className="border-l-2 border-gray-200 pl-3">
                    <h5 className="text-sm font-medium text-gray-900">{exp?.position}</h5>
                    <p className="text-xs text-gray-600">{exp?.company} • {exp?.duration}</p>
                    {exp?.description && (
                      <p className="text-xs text-gray-700 mt-1">{exp?.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {latestParsedData?.education && Array.isArray(latestParsedData?.education) && latestParsedData?.education?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <GraduationCap className="h-4 w-4 mr-2 text-green-500" />
                Education
              </h4>
              <div className="space-y-2">
                {latestParsedData?.education?.map((edu, index) => (
                  <div key={index}>
                    <p className="text-sm font-medium text-gray-900">{edu?.degree}</p>
                    <p className="text-xs text-gray-600">{edu?.institution} • {edu?.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications & Languages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {latestParsedData?.certifications && latestParsedData?.certifications?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <Award className="h-4 w-4 mr-2 text-orange-500" />
                  Certifications
                </h4>
                <div className="space-y-1">
                  {latestParsedData?.certifications?.map((cert, index) => (
                    <p key={index} className="text-xs text-gray-700">{cert}</p>
                  ))}
                </div>
              </div>
            )}

            {latestParsedData?.languages && latestParsedData?.languages?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                  <Globe className="h-4 w-4 mr-2 text-blue-500" />
                  Languages
                </h4>
                <div className="flex flex-wrap gap-1">
                  {latestParsedData?.languages?.map((lang, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Analysis Metadata */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Analyzed by {latestParsedData?.ai_model_used || 'AI'} • 
                Confidence: {Math.round((latestParsedData?.parsing_confidence_score || 0) * 100)}%
              </span>
              <span>
                {new Date(latestParsedData?.created_at)?.toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">
            No parsed resume data available. Upload a resume to get AI-powered analysis.
          </p>
        </div>
      )}
    </div>
  );
};

export default ParsedResumeViewer;